// Run the BecomingLit envmap shading (shadeGaussiansEnvmap) over a mesh's
// vertices instead of Gaussian splats — the trivial mesh adaptation: a vertex's
// world position + normal IS the "Gaussian" point the equation shades. Writes
// per-vertex linear RGB, consumed as a `color` attribute (vertexColors).
//
// Diffuse + specular samplers are the SAME closures the hair pipeline feeds
// shadeGaussiansEnvmap, so this ball is a faithful oracle for how the equation
// + the baked env volumes will light the hair.

import * as THREE from 'three';
import { shadeGaussiansEnvmap } from '../hair/shading.js';

// Transform local positions (column-major 4×4 matrixWorld) and normals
// (column-major 3×3 normalMatrix) to unit world-space. Pure — no THREE, no
// allocation beyond the caller-provided outputs. THREE.Matrix4/Matrix3 `.elements`
// are column-major: m[col*4+row] / m[col*3+row].
export function transformToWorld(positions, normals, count, matrixWorld, normalMatrix, outPos, outNrm) {
  const m = matrixWorld, nm = normalMatrix;
  for (let i = 0; i < count; i++) {
    const i3 = i * 3;
    const x = positions[i3], y = positions[i3 + 1], z = positions[i3 + 2];
    outPos[i3]     = m[0] * x + m[4] * y + m[8] * z + m[12];
    outPos[i3 + 1] = m[1] * x + m[5] * y + m[9] * z + m[13];
    outPos[i3 + 2] = m[2] * x + m[6] * y + m[10] * z + m[14];

    const nx = normals[i3], ny = normals[i3 + 1], nz = normals[i3 + 2];
    const wx = nm[0] * nx + nm[3] * ny + nm[6] * nz;
    const wy = nm[1] * nx + nm[4] * ny + nm[7] * nz;
    const wz = nm[2] * nx + nm[5] * ny + nm[8] * nz;
    const len = Math.hypot(wx, wy, wz) || 1;
    outNrm[i3]     = wx / len;
    outNrm[i3 + 1] = wy / len;
    outNrm[i3 + 2] = wz / len;
  }
}

// Broadcast a scalar/[r,g,b] albedo, scalar roughness and scalar specVis into
// the per-vertex arrays shadeGaussiansEnvmap indexes. Reuses `dst.*` buffers
// across calls (keyed by count) so a per-frame re-shade allocates nothing.
function ensureMaterialArrays(dst, count, albedo, roughness, specVis) {
  if (!dst.albedo || dst.albedo.length !== count * 3) {
    dst.albedo = new Float32Array(count * 3);
    dst.roughness = new Float32Array(count);
    dst.specVis = new Float32Array(count);
  }
  const ar = Array.isArray(albedo) ? albedo[0] : albedo;
  const ag = Array.isArray(albedo) ? albedo[1] : albedo;
  const ab = Array.isArray(albedo) ? albedo[2] : albedo;
  for (let i = 0; i < count; i++) {
    dst.albedo[i * 3] = ar; dst.albedo[i * 3 + 1] = ag; dst.albedo[i * 3 + 2] = ab;
    dst.roughness[i] = roughness;
    dst.specVis[i] = specVis;
  }
  return dst;
}

// Shade `mesh` in place. opts: { albedo:[r,g,b]|number, roughness, specVis,
// campos:[x,y,z], diffuseEnv, specularEnv, fgLut }. The mesh must have a
// position + normal attribute; its world matrix should be current.
export function shadeMeshEnvmap(mesh, opts) {
  const geom = mesh.geometry;
  const posAttr = geom.getAttribute('position');
  const nrmAttr = geom.getAttribute('normal');
  if (!posAttr || !nrmAttr) return;
  const count = posAttr.count;

  // Per-mesh scratch lives on geometry.userData so re-shades reuse buffers.
  const s = geom.userData._envShade ?? (geom.userData._envShade = {});
  if (!s.worldPos || s.worldPos.length !== count * 3) {
    s.worldPos = new Float32Array(count * 3);
    s.worldNrm = new Float32Array(count * 3);
    s.out = new Float32Array(count * 3);
  }

  mesh.updateWorldMatrix(true, false);
  const nmat = (s.nmat ?? (s.nmat = new THREE.Matrix3())).getNormalMatrix(mesh.matrixWorld);
  transformToWorld(posAttr.array, nrmAttr.array, count, mesh.matrixWorld.elements, nmat.elements, s.worldPos, s.worldNrm);

  const mat = ensureMaterialArrays(s, count, opts.albedo, opts.roughness, opts.specVis);
  shadeGaussiansEnvmap({
    pos: s.worldPos,
    nrm: s.worldNrm,
    albedo: mat.albedo,
    roughness: mat.roughness,
    specVis: mat.specVis,
    campos: opts.campos,
    neuralBsdfReflectance: null,
    diffuseEnv: opts.diffuseEnv,
    specularEnv: opts.specularEnv,
    fgLut: opts.fgLut,
    useNeuralBsdf: false,
    outColors: s.out,
    N: count,
  });

  let colorAttr = geom.getAttribute('color');
  if (!colorAttr || colorAttr.count !== count) {
    colorAttr = new THREE.BufferAttribute(new Float32Array(count * 3), 3);
    geom.setAttribute('color', colorAttr);
  }
  colorAttr.array.set(s.out);
  colorAttr.needsUpdate = true;
}
